// =============================================================================
// VandieZ agent-web -- Trip detail / tracking (/trips/:tripId)
// =============================================================================
// Heavy copy of shipper-web/src/pages/TripDetailPage.tsx.
//
// Adaptations vs shipper-web:
//   * All tripsApi calls thread currentOwnerId (?as=) via the agent API client.
//   * 15s GPS polling pattern is unchanged.
//   * "Trip contacts" heading shows "(for <ownerName>)" so the agent knows
//     whose trip this is when switching between owners.
//   * Status update and PoD submit buttons are fully READ-ONLY in v1:
//     - agent permission trips:status_update + trips:pod_submit are backend-
//       supported via require_permission, but the UI is deferred to Phase 2.
//     - If the current agent owner grants status_update or pod_submit, buttons
//       are shown but marked disabled with a tooltip for now.
//   * Back link goes to /trips?as=<currentOwnerId>.
//   * Header uses <AppHeader title="Trip detail" />.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  MapPin,
  Navigation,
  Phone,
  Truck,
} from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { tripsApi } from "@/api/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { useOwnerContext } from "@/lib/owner-context";
import { ApiError } from "@/types/auth";
import type {
  GpsPing,
  SettlementStatement,
  Trip,
  TripStatus,
} from "@/types/trip";

const STATUS_FLOW: TripStatus[] = [
  "booked",
  "en_route_to_pickup",
  "arrived_at_pickup",
  "loading",
  "in_transit",
  "arrived_at_drop",
  "unloading",
  "delivered",
  "completed",
];

const STATUS_LABEL: Record<TripStatus, string> = {
  booked: "Booked",
  en_route_to_pickup: "En route to pickup",
  arrived_at_pickup: "At pickup",
  loading: "Loading",
  in_transit: "In transit",
  arrived_at_drop: "At drop",
  unloading: "Unloading",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
};

function formatDate(iso: string | null): string {
  if (iso === null) {return "—";}
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatINR(amount: string): string {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {return amount;}
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function statusIndex(s: TripStatus): number {
  const i = STATUS_FLOW.indexOf(s);
  return i === -1 ? -1 : i;
}

export function TripDetailPage(): React.JSX.Element {
  const { tripId } = useParams<{ tripId: string }>();
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuth();
  const { currentOwnerId, currentOwner } = useOwnerContext();

  const [trip, setTrip] = useState<Trip | null>(null);
  const [lastPing, setLastPing] = useState<GpsPing | null>(null);
  const [pingsTotal, setPingsTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // C-21. Loaded once rather than polled with the trip: ledger lines are
  // written at completion and never change afterwards, so re-fetching them
  // every poll tick would be traffic for a value that cannot move.
  const [settlement, setSettlement] = useState<SettlementStatement | null>(
    null,
  );

  // Use a ref for the polling timer so the cleanup function in useEffect
  // can clear it even if the latest closure changes.
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolve the effective owner id: URL ?as= wins (via context), fall back to
  // currentOwnerId from context.
  const asParam = searchParams.get("as");
  const effectiveOwnerId = asParam ?? currentOwnerId;

  useEffect(() => {
    if (accessToken === null || tripId === undefined) {return;}
    const controller = new AbortController();
    const token = accessToken;
    const id = tripId;
    void (async () => {
      try {
        setSettlement(await tripsApi.settlement(token, id, controller.signal));
      } catch {
        // Deliberately silent. The statement is supplementary to the trip;
        // a trip that has not settled yet is the normal case, and an error
        // banner over the whole page would misrepresent that as a failure.
        setSettlement(null);
      }
    })();
    return () => { controller.abort(); };
  }, [accessToken, tripId]);

  useEffect(() => {
    if (accessToken === null || tripId === undefined || effectiveOwnerId === null) {return;}
    const controller = new AbortController();
    let cancelled = false;
    const token = accessToken;
    const id = tripId;
    const ownerId = effectiveOwnerId;

    let latest: Trip | null = null;

    async function pollOnce(): Promise<void> {
      if (cancelled) {return;}
      try {
        const t = await tripsApi.detail(token, id, ownerId, controller.signal);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated by cleanup
        if (cancelled) {return;}
        latest = t;
        setTrip(t);
        setError(null);
        if (
          t.status !== "booked"
          && t.status !== "cancelled"
          && t.status !== "completed"
        ) {
          try {
            const gps = await tripsApi.gpsHistory(
              token,
              id,
              ownerId,
              controller.signal,
            );
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated by cleanup
            if (cancelled) {return;}
            setPingsTotal(gps.total);
            setLastPing(gps.items[0] ?? null);
          } catch {
            // GPS being absent is non-fatal; the trip view stays up.
          }
        }
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated by cleanup
        if (cancelled || controller.signal.aborted) {return;}
        setError(
          err instanceof ApiError ? err.problem.detail : "Couldn't load trip.",
        );
      } finally {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated by cleanup
        if (!cancelled) {
          setIsLoading(false);
          const s = latest?.status;
          if (s !== "completed" && s !== "cancelled" && s !== "delivered") {
            pollTimerRef.current = setTimeout(() => { void pollOnce(); }, 15_000);
          }
        }
      }
    }

    void pollOnce();
    return () => {
      cancelled = true;
      controller.abort();
      if (pollTimerRef.current !== null) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [accessToken, tripId, effectiveOwnerId]);

  const backUrl = `/trips${effectiveOwnerId !== null ? `?as=${effectiveOwnerId}` : ""}`;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AppHeader title="Trip detail" />
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (trip === null) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AppHeader title="Trip detail" />
        <div className="mx-auto max-w-3xl px-6 py-10">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error ?? "Trip not found."}</AlertDescription>
          </Alert>
          <Link to={backUrl} className="mt-4 inline-block">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4" />
              Back to trips
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentIdx = statusIndex(trip.status);
  const ownerName = currentOwner?.owner_full_name ?? "this owner";

  return (
    <div className="min-h-screen bg-muted/30 pb-16">
      <AppHeader title="Trip detail" />

      <main className="mx-auto max-w-4xl space-y-6 px-6 py-8">
        <div>
          <Link to={backUrl}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
              Back to trips
            </Button>
          </Link>
        </div>

        {trip.status === "cancelled" ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This trip was cancelled at {formatDate(trip.cancelled_at)}.
            </AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Status</CardTitle>
            <CardDescription>
              {trip.status === "completed" || trip.status === "cancelled"
                ? "This trip is no longer active."
                : "Auto-refreshes every 15s."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              {STATUS_FLOW.map((s, i) => {
                const done = currentIdx >= i;
                const active = s === trip.status;
                return (
                  <li key={s} className="flex items-center gap-3">
                    {done ? (
                      <CheckCircle2 className="h-5 w-5 text-success" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                    <span
                      className={`text-sm ${active ? "font-semibold" : done ? "text-foreground" : "text-muted-foreground"}`}
                    >
                      {STATUS_LABEL[s]}
                    </span>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />
              Live location
            </CardTitle>
            <CardDescription>
              {lastPing === null
                ? "Driver hasn't pinged yet."
                : `Last update ${formatDate(lastPing.recorded_at)} · ${pingsTotal} pings total`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {lastPing === null ? (
              <p className="text-sm text-muted-foreground">
                GPS pings start once the driver is en route to pickup.
              </p>
            ) : (
              <div className="space-y-2 text-sm">
                <p className="font-mono">
                  {lastPing.lat}, {lastPing.lng}
                </p>
                {lastPing.speed_kmh !== null ? (
                  <p className="text-xs text-muted-foreground">
                    Moving at {Math.round(lastPing.speed_kmh)} km/h
                  </p>
                ) : null}
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${lastPing.lat},${lastPing.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Navigation className="h-3 w-3" />
                  Open in Google Maps
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Truck className="h-4 w-4" />
              Trip contacts (for {ownerName})
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Pickup
              </p>
              <p className="text-sm font-medium">
                {trip.pickup_contact_name ?? "—"}
              </p>
              {trip.pickup_contact_phone !== null ? (
                <a
                  href={`tel:${trip.pickup_contact_phone}`}
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Phone className="h-3 w-3" />
                  {trip.pickup_contact_phone}
                </a>
              ) : null}
              <p className="font-mono text-xs text-muted-foreground">
                {trip.pickup_lat}, {trip.pickup_lng}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Drop
              </p>
              <p className="text-sm font-medium">
                {trip.drop_contact_name ?? "—"}
              </p>
              {trip.drop_contact_phone !== null ? (
                <a
                  href={`tel:${trip.drop_contact_phone}`}
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Phone className="h-3 w-3" />
                  {trip.drop_contact_phone}
                </a>
              ) : null}
              <p className="font-mono text-xs text-muted-foreground">
                {trip.drop_lat}, {trip.drop_lng}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-4 w-4" />
              Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Booked</dt>
                <dd>{formatDate(trip.booked_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Picked up</dt>
                <dd>{formatDate(trip.picked_up_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Delivered</dt>
                <dd>{formatDate(trip.delivered_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Completed</dt>
                <dd>{formatDate(trip.completed_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Trip value</dt>
                <dd>{formatINR(trip.trip_value)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Platform commission</dt>
                <dd>{formatINR(trip.platform_commission_inr)}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* C-21: what this trip settled for ME. Only rendered once
            something has actually moved -- an empty card reading "₹0"
            would look like a trip that earned nothing rather than one
            that has not settled yet. */}
        {settlement !== null && settlement.is_settled ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Settlement</CardTitle>
              <CardDescription>
                Your side of this trip, taken straight from the ledger.
                Other parties&rsquo; entries are not shown.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <dl className="grid gap-3 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-muted-foreground">Received</dt>
                  <dd>{formatINR(settlement.gross_inr)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Deducted</dt>
                  <dd>{formatINR(settlement.deductions_inr)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Net</dt>
                  <dd className="font-medium">{formatINR(settlement.net_inr)}</dd>
                </div>
              </dl>
              <ul className="space-y-1 text-sm">
                {settlement.lines.map((line, i) => (
                  <li
                    key={`${line.entry_type}-${line.created_at}-${String(i)}`}
                    className="flex justify-between gap-4 border-t pt-1"
                  >
                    <span className="text-muted-foreground">
                      {line.notes ?? line.entry_type.replace(/_/g, " ")}
                    </span>
                    <span>{formatINR(line.amount)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}
      </main>
    </div>
  );
}
